import { useMsal } from "@azure/msal-react";
import "primeflex/primeflex.css";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Toolbar as ReactToolbar } from "primereact/toolbar";
import React, { useContext, useState } from "react";
import { SiteContext } from "../../contexts/SiteContext";
import { UserProfileContext } from "../../contexts/UserProfileContext";
import { useAppContext } from "../../contexts/AppContext";
import { Messages } from "../../enums/Messages";
import useConfig from "../../hooks/useConfig";
import "../../styles/components/Toolbar.scss";
import { getNameForDisplay } from "../../utils/utils";
import ConfirmationModal from "../global/ConfirmationModal";
import { Modal } from "./Modal";
import Icon, { IconNames } from "./Icon";
import { performLogout } from "../../utils/auth/logoutUtils";
import { getLoggedInUserSpecialtyType } from "../../utils/userContextUtils";

interface ToolbarProps {
  isPracticeInfoVisible?: boolean;
  isSiteInfoVisible?: boolean;
  centerContent?: React.ReactNode;
}

const Toolbar: React.FC<ToolbarProps> = ({
  isPracticeInfoVisible = true,
  isSiteInfoVisible = true,
  centerContent,
}) => {
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [isLoadingModalVisible, setIsLoadingModalVisible] = useState(false);
  const { instance, accounts: _accounts } = useMsal();
  const { data: configData } = useConfig();
  const {
    currentPracticeDetails,
    currentSite,
    currentStationName,
    isValidSite,
    isValidPractice,
  } = useContext(SiteContext);

  const { user } = useContext(UserProfileContext);
  const { pageTitle } = useAppContext();

  const getUserEmail = () => {
    return user?.contactEmail || "";
  };

  const noContinue = () => {
    setIsLogoutModalVisible(false);
  };

  const handleLogout = () => {
    setIsLogoutModalVisible(false);
    setIsLoadingModalVisible(true);
    setTimeout(() => {
      performLogout({
        instance,
        configData: configData || {},
        userSpecialtyTypeId: getLoggedInUserSpecialtyType(user)?.id ?? 0,
        onComplete: () => {
          setIsLoadingModalVisible(false);
        },
      });
    }, 2000);
  };

  const renderPracticeSiteStationInfo = (
    iconName: string,
    labelText: string,
  ) => {
    return (
      <div className="flex-initial flex align-items-center">
        <Icon
          iconName={iconName}
          className={`mr-1 primary-text text-small-medium`}
        />
        <span className="toolbar-text text-small-medium ">{labelText}</span>
      </div>
    );
  };

  const startContent = () => {
    return (
      <div className="flex align-items-center">
        {isPracticeInfoVisible &&
          isValidPractice &&
          currentPracticeDetails &&
          renderPracticeSiteStationInfo(
            IconNames.buildingIcon,
            getNameForDisplay(
              currentPracticeDetails as {
                displayName: string | null;
                name: string;
              },
            ),
          )}
        {isSiteInfoVisible && isValidSite && (
          <>
            <Divider layout="vertical" className="p-1" />
            {renderPracticeSiteStationInfo(
              IconNames.locationIcon,
              currentSite?.siteDetail?.displayName ??
                currentSite?.siteDetail?.name ??
                "",
            )}
          </>
        )}
        {currentStationName && (
          <>
            <Divider layout="vertical" className="p-1" />
            {renderPracticeSiteStationInfo(
              IconNames.systemIcon,
              currentStationName,
            )}
          </>
        )}
        {pageTitle && (
          <>
            {/* <Divider layout="vertical" className="p-1" /> */}
            <div className="toolbar-page-title ml-3">{pageTitle.title}</div>
          </>
        )}
      </div>
    );
  };

  const centerContentRender = () => {
    return centerContent || null;
  };

  const loadingModalContent = () => {
    return (
      <div className="flex justify-content-center align-items-center gap-4 pt-3">
        <span className="global-modal-header-text font-bold">
          {"Logging Out"}
        </span>
        <i className={"flex global-modal-header-text pi pi-spin pi-spinner"} />
      </div>
    );
  };
  const rightContents = (
    <>
      <div className="text-right mr-3">
        <p className="text-bold m-0 mt-1">
          {user?.firstName} {user?.lastName}
        </p>
        <p className="m-0 mb-1 user-email">{getUserEmail()}</p>
      </div>
      <Button
        icon="pi pi-sign-out"
        className="primary-text logout-button"
        rounded
        text
        tooltip="Logout"
        tooltipOptions={{ position: "left" }}
        id="btn-logout"
        aria-label="logout"
        onClick={() => setIsLogoutModalVisible(true)}
      />
    </>
  );

  return (
    <>
      <div className="toolbar-container">
        <ReactToolbar
          start={startContent}
          center={centerContentRender}
          end={rightContents}
          className="toolbar"
        />
      </div>
      <ConfirmationModal
        isVisible={isLogoutModalVisible}
        setIsVisible={setIsLogoutModalVisible}
        isIcon={false}
        primaryButtonLabel="YES, LOG OUT"
        primaryButtonOnClick={handleLogout}
        secondaryButtonLabel="NO, CONTINUE WORKING"
        secondaryButtonOnClick={noContinue}
        headerText={""}
        bodyContent={
          <h5 className="font-bold m-0">{Messages.LOGOUT_CONFIRM_HEADER}</h5>
        }
        bodyText={""}
        className={"logout-confirm-modal"}
      />

      <Modal
        visible={isLoadingModalVisible}
        setVisible={setIsLoadingModalVisible}
        isClosable={false}
        bodyContent={loadingModalContent()}
        headerText=""
        className="logout-spinner-modal"
      />
    </>
  );
};

export default Toolbar;
